import React, { useCallback, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    kakao: any;
  }
}

// 전역 SDK 로딩 상태 관리
let kakaoSDKLoadPromise: Promise<void> | null = null;

// 전역 지도 인스턴스 추적 (중복 생성 방지)
const activeMapInstances = new WeakMap<HTMLDivElement, boolean>();

const RESTAURANT_MARKER_SVG = `
<svg width="32" height="48" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M16 0C7.16344 0 0 7.16344 0 16C0 28 16 42 16 42C16 42 32 28 32 16C32 7.16344 24.8366 0 16 0Z" fill="#FF6B35"/>
  <circle cx="16" cy="16" r="11" fill="white"/>
  <path d="M11.5 10V14C11.5 15.1 12.4 16 13.5 16V22H14.5V16C15.6 16 16.5 15.1 16.5 14V10H15.5V13H14.5V10H13.5V13H12.5V10H11.5ZM19.5 10C18.9 10 18.5 10.4 18.5 11V22H19.5V14C20.1 14 20.5 13.6 20.5 13V10H19.5Z" fill="#FF6B35"/>
</svg>
`;

const getKakaoApiKey = () => {
  const key = process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY || process.env.REACT_APP_KAKAO_MAP_API_KEY;
  if (!key) {
    throw new Error('카카오맵 API 키가 설정되지 않았습니다. 환경변수를 확인해주세요.');
  }
  return key;
};

// 카카오맵 SDK 로딩 함수 (개선된 버전)
const loadKakaoMapScript = (): Promise<void> => {
  // 이미 로딩 중이면 기존 Promise 반환
  if (kakaoSDKLoadPromise) {
    return kakaoSDKLoadPromise;
  }

  kakaoSDKLoadPromise = new Promise((resolve, reject) => {
    // 이미 완전히 로드되었으면 바로 resolve
    if (window.kakao && window.kakao.maps && window.kakao.maps.Map) {
      console.log('✅ 카카오맵 SDK 이미 로드됨');
      resolve();
      return;
    }

    let apiKey: string;
    try {
      apiKey = getKakaoApiKey();
    } catch (error) {
      reject(error instanceof Error ? error : new Error('카카오맵 API 키가 없습니다'));
      return;
    }

    // 기존 스크립트 제거 (문제가 있는 경우)
    const existingScripts = document.querySelectorAll('script[src*="dapi.kakao.com"]');
    existingScripts.forEach(script => {
      if (!window.kakao || !window.kakao.maps) {
        console.log('🔄 기존 문제 스크립트 제거');
        script.remove();
      }
    });

    // 스크립트가 존재하고 정상 로드된 경우
    if (window.kakao) {
      if (window.kakao.maps && window.kakao.maps.Map) {
        console.log('✅ 기존 스크립트로 SDK 완전 로드됨');
        resolve();
        return;
      } else if (window.kakao.maps) {
        // kakao.maps.load 호출 필요
        console.log('🔄 기존 스크립트에서 maps 라이브러리 로드');
        window.kakao.maps.load(() => {
          console.log('✅ 기존 스크립트로 maps 라이브러리 로드 완료');
          resolve();
        });
        return;
      }
    }

    console.log('🚀 새 카카오맵 스크립트 로딩 시작...');

    // 새 스크립트 생성 (autoload 옵션 추가로 document.write 문제 해결)
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&libraries=services,clusterer&autoload=false`;
    script.async = true;
    
    const timeout = setTimeout(() => {
      console.error('❌ 카카오맵 스크립트 로딩 타임아웃 (15초)');
      kakaoSDKLoadPromise = null; // 재시도 가능하도록 초기화
      reject(new Error('카카오맵 스크립트 로딩 타임아웃'));
    }, 15000);
    
    script.onload = () => {
      clearTimeout(timeout);
      console.log('📦 카카오맵 스크립트 로드 완료');
      
      // autoload=false이므로 수동으로 라이브러리 로드
      if (window.kakao) {
        console.log('🔄 카카오맵 라이브러리 수동 로드 시작...');
        window.kakao.maps.load(() => {
          console.log('✅ 카카오맵 라이브러리 로드 완료');
          resolve();
        });
      } else {
        console.error('❌ 카카오 객체를 찾을 수 없습니다');
        kakaoSDKLoadPromise = null;
        reject(new Error('카카오 객체를 찾을 수 없습니다'));
      }
    };
    
    script.onerror = (error) => {
      clearTimeout(timeout);
      console.error('❌ 카카오맵 스크립트 로드 실패:', error);
      console.error('API 키:', apiKey ? `${apiKey.substring(0, 8)}...` : '없음');
      console.error('스크립트 URL:', script.src);
      kakaoSDKLoadPromise = null;
      reject(new Error('카카오맵 API 키가 유효하지 않거나 네트워크 오류가 발생했습니다'));
    };
    
    document.head.appendChild(script);
  });

  return kakaoSDKLoadPromise;
};

export interface MapMarker {
  id: string;
  name?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  subAdd1?: string;
  subAdd2?: string;
}

interface ClusterGroup {
  markers: Array<{ marker: MapMarker; position: any; coords: { lat: number; lng: number } }>;
  center: { lat: number; lng: number };
}

export interface UserLocation {
  latitude: number;
  longitude: number;
  label?: string;
}

interface KakaoMapProps {
  latitude?: number;
  longitude?: number;
  address?: string;
  width?: string | number;
  height?: string | number;
  level?: number;
  className?: string;
  restaurantName?: string;
  subAdd1?: string;
  subAdd2?: string;
  markers?: MapMarker[];
  focusMarkerId?: string;
  fitBounds?: boolean;
  userLocation?: UserLocation | null;
  showUserLocation?: boolean;
  onMarkerClick?: (marker: MapMarker) => void;
  initialCenter?: { latitude: number; longitude: number };
  initialLevel?: number;
  preserveView?: boolean;
  onMapViewChange?: (view: { latitude: number; longitude: number; level: number }) => void;
}

const KakaoMap: React.FC<KakaoMapProps> = ({
  latitude,
  longitude,
  address,
  width = '100%',
  height = '400px',
  level = 3,
  className = '',
  restaurantName,
  subAdd1,
  subAdd2,
  markers,
  focusMarkerId,
  fitBounds = true,
  userLocation,
  showUserLocation = true,
  onMarkerClick,
  initialCenter,
  initialLevel,
  preserveView = false,
  onMapViewChange,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mapInstance = useRef<any>(null);
  const mapMarkersRef = useRef<any[]>([]);
  const overlaysRef = useRef<any[]>([]);
  const geocoderRef = useRef<any>(null);
  const placesRef = useRef<any>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const initialViewRef = useRef<{ lat: number; lng: number; level: number } | null>(null);
  const fullscreenViewRef = useRef<{ lat: number; lng: number; level: number } | null>(null);
  const idleHandlerRef = useRef<(() => void) | null>(null);
  const userInteractedRef = useRef(false);
  const lastMarkerSignatureRef = useRef<string>('');
  const isInitializingRef = useRef(false);
  const currentLevelRef = useRef<number>(level);
  const validPositionsRef = useRef<Array<{ marker: MapMarker; position: any; coords: { lat: number; lng: number } }>>([]);
  const zoomHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // cleanup 함수에서 사용할 컨테이너 참조를 미리 저장
    const containerElement = mapContainer.current;

    // 이미 초기화 중이거나 완료된 경우 중복 실행 방지
    if (isInitializingRef.current || mapInstance.current) {
      console.log('⚠️ KakaoMap 이미 초기화됨 또는 초기화 중, 중복 실행 방지');
      return;
    }

    // 같은 컨테이너에 대한 중복 초기화 방지
    if (containerElement && activeMapInstances.has(containerElement)) {
      console.log('⚠️ 같은 컨테이너에 대한 중복 초기화 방지');
      return;
    }

    let apiKey: string;
    try {
      apiKey = getKakaoApiKey();
    } catch (err) {
      const message = err instanceof Error ? err.message : '카카오맵 API 키가 설정되지 않았습니다. 관리자에게 문의하세요.';
      setError(message);
      return;
    }

    console.log('🗺️ KakaoMap 초기화:', {
      container: !!mapContainer.current,
      apiKey: apiKey ? `${apiKey.substring(0, 8)}...` : '❌ 없음',
      latitude,
      longitude,
      address,
      restaurantName
    });

    // 기본 검증
    if (!mapContainer.current) {
      setError('지도 컨테이너 요소가 없습니다');
      return;
    }

    // 이전 에러 상태 초기화
    setError(null);
    isInitializingRef.current = true;

    const initializeMap = async () => {
      try {
        console.log('🚀 카카오맵 SDK 로딩 시작...');
        await loadKakaoMapScript();
        console.log('✅ 카카오맵 SDK 로딩 완료, 지도 초기화 시작...');

        if (!mapContainer.current) {
          throw new Error('지도 컨테이너가 사라졌습니다');
        }

        // 카카오맵 객체 확인 및 대기
        let retryCount = 0;
        const maxRetries = 10;
        
        while (retryCount < maxRetries) {
          const { kakao } = window;
          if (kakao?.maps?.Map && kakao?.maps?.services) {
            console.log('✅ 카카오맵 객체 확인 완료');
            break;
          }
          
          console.log(`🔄 카카오맵 객체 대기 중... (${retryCount + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 200));
          retryCount++;
        }
        
        const { kakao } = window;
        if (!kakao?.maps?.Map) {
          console.error('❌ 카카오맵 객체 상태:', {
            kakao: !!kakao,
            maps: !!kakao?.maps,
            Map: !!kakao?.maps?.Map,
            services: !!kakao?.maps?.services
          });
          throw new Error('카카오맵 객체 초기화 실패 - API 키가 유효하지 않거나 서비스가 제한되었을 수 있습니다');
        }

        // 지도 생성 (초기 중심: 서울시청)
        const defaultCenter = new kakao.maps.LatLng(37.5665, 126.9780);
        const map = new kakao.maps.Map(mapContainer.current, { 
          center: defaultCenter, 
          level: typeof initialLevel === 'number' ? initialLevel : level,
          draggable: true,
          scrollwheel: true
        });

        mapInstance.current = map;
        geocoderRef.current = new kakao.maps.services.Geocoder();
        placesRef.current = new kakao.maps.services.Places();
        
        // 전역 인스턴스 추적에 추가
        if (mapContainer.current) {
          activeMapInstances.set(mapContainer.current, true);
        }
        if (initialCenter && typeof initialCenter.latitude === 'number' && typeof initialCenter.longitude === 'number') {
          const providedCenter = new kakao.maps.LatLng(initialCenter.latitude, initialCenter.longitude);
          map.setCenter(providedCenter);
        }
        if (typeof initialLevel === 'number') {
          map.setLevel(initialLevel);
        }
        initialViewRef.current = initialCenter
          ? {
              lat: initialCenter.latitude,
              lng: initialCenter.longitude,
              level: typeof initialLevel === 'number' ? initialLevel : map.getLevel(),
            }
          : null;

        if (onMapViewChange) {
          const handleIdle = () => {
            const center = map.getCenter();
            onMapViewChange({
              latitude: center.getLat(),
              longitude: center.getLng(),
              level: map.getLevel(),
            });
          };
          idleHandlerRef.current = handleIdle;
          kakao.maps.event.addListener(map, 'idle', handleIdle);
        }

        kakao.maps.event.addListener(map, 'dragstart', () => {
          userInteractedRef.current = true;
        });

        const handleZoomChanged = () => {
          userInteractedRef.current = true;
          const newLevel = map.getLevel();
          currentLevelRef.current = newLevel;

          if (validPositionsRef.current.length > 0) {
            renderMarkersWithClustering(validPositionsRef.current, map, newLevel);
          }
        };

        zoomHandlerRef.current = handleZoomChanged;
        kakao.maps.event.addListener(map, 'zoom_changed', handleZoomChanged);
        console.log('🗺️ 지도 객체 생성 완료');
        // 지도 컨트롤 추가
        const mapTypeControl = new kakao.maps.MapTypeControl();
        map.addControl(mapTypeControl, kakao.maps.ControlPosition.TOPRIGHT);

        const zoomControl = new kakao.maps.ZoomControl();
        map.addControl(zoomControl, kakao.maps.ControlPosition.RIGHT);

        setMapLoaded(true);
        console.log('✅ 카카오맵 초기화 완료');
        isInitializingRef.current = false;

      } catch (error) {
        console.error('❌ 카카오맵 초기화 실패:', error);
        setError(error instanceof Error ? error.message : '알 수 없는 오류');
        setMapLoaded(false);
        isInitializingRef.current = false;
      }
    };

    initializeMap();

    return () => {
      if (mapInstance.current && window.kakao?.maps?.event) {
        if (idleHandlerRef.current) {
          window.kakao.maps.event.removeListener(mapInstance.current, 'idle', idleHandlerRef.current);
          idleHandlerRef.current = null;
        }
        if (zoomHandlerRef.current) {
          window.kakao.maps.event.removeListener(mapInstance.current, 'zoom_changed', zoomHandlerRef.current);
          zoomHandlerRef.current = null;
        }
      }
      overlaysRef.current.forEach((overlay) => overlay.setMap(null));
      overlaysRef.current = [];
      mapMarkersRef.current = [];
      validPositionsRef.current = [];
      
      if (containerElement) {
        activeMapInstances.delete(containerElement);
      }
      
      mapInstance.current = null;
      setMapLoaded(false);
      isInitializingRef.current = false;
    };
    // 초기 1회만 실행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapInstance.current || !window.kakao) return;
    const { kakao } = window;
    const map = mapInstance.current;

    if (initialCenter && typeof initialCenter.latitude === 'number' && typeof initialCenter.longitude === 'number') {
      const center = new kakao.maps.LatLng(initialCenter.latitude, initialCenter.longitude);
      map.setCenter(center);
    }

    if (typeof initialLevel === 'number') {
      map.setLevel(initialLevel);
    }
  }, [initialCenter, initialCenter?.latitude, initialCenter?.longitude, initialLevel]);

  const clearMapObjects = () => {
    mapMarkersRef.current.forEach((marker) => marker.setMap(null));
    mapMarkersRef.current = [];
    overlaysRef.current.forEach((overlay) => overlay.setMap(null));
    overlaysRef.current = [];
  };

  const ensureGeocoder = () => {
    const { kakao } = window;
    if (!kakao?.maps?.services) return null;
    if (!geocoderRef.current) {
      geocoderRef.current = new kakao.maps.services.Geocoder();
    }
    return geocoderRef.current;
  };

  const ensurePlaces = () => {
    const { kakao } = window;
    if (!kakao?.maps?.services) return null;
    if (!placesRef.current) {
      placesRef.current = new kakao.maps.services.Places();
    }
    return placesRef.current;
  };

  const resolveCoordinates = async (marker: MapMarker): Promise<{ lat: number; lng: number } | null> => {
    const { kakao } = window;
    if (!kakao?.maps?.services) {
      return null;
    }

    if (typeof marker.latitude === 'number' && isFinite(marker.latitude) &&
        typeof marker.longitude === 'number' && isFinite(marker.longitude)) {
      return { lat: marker.latitude, lng: marker.longitude };
    }

    const geocoder = ensureGeocoder();
    const places = ensurePlaces();

    if (!geocoder || !places) {
      return null;
    }

    const tryAddress = async (query?: string | null) => {
      if (!query || !geocoder) return null;
      const trimmed = query.trim();
      if (!trimmed) return null;

      return new Promise<{ lat: number; lng: number } | null>((resolve) => {
        geocoder.addressSearch(trimmed, (result: any, status: any) => {
          if (status === kakao.maps.services.Status.OK && Array.isArray(result) && result.length > 0 && result[0]?.y && result[0]?.x) {
            const lat = parseFloat(result[0].y);
            const lng = parseFloat(result[0].x);
            if (isFinite(lat) && isFinite(lng)) {
              resolve({ lat, lng });
            } else {
              resolve(null);
            }
          } else {
            resolve(null);
          }
        });
      });
    };

    const tryKeyword = async (keyword?: string | null) => {
      if (!keyword || !places) return null;
      const trimmed = keyword.trim();
      if (!trimmed) return null;

      return new Promise<{ lat: number; lng: number } | null>((resolve) => {
        places.keywordSearch(trimmed, (data: any, status: any) => {
          if (status === kakao.maps.services.Status.OK && Array.isArray(data) && data.length > 0 && data[0]?.y && data[0]?.x) {
            const lat = parseFloat(data[0].y);
            const lng = parseFloat(data[0].x);
            if (isFinite(lat) && isFinite(lng)) {
              resolve({ lat, lng });
            } else {
              resolve(null);
            }
          } else {
            resolve(null);
          }
        });
      });
    };

    // 1. 주소로 먼저 시도
    if (marker.address) {
      const addressResult = await tryAddress(marker.address);
      if (addressResult) return addressResult;
    }

    // 2. 지역명 + 음식점명으로 키워드 검색
    const combinedKeyword = [marker.subAdd1, marker.subAdd2, marker.name]
      .filter(Boolean)
      .join(' ');

    if (combinedKeyword) {
      const keywordResult = await tryKeyword(combinedKeyword);
      if (keywordResult) return keywordResult;
    }

    // 3. 음식점명만으로 검색
    if (marker.name) {
      const nameResult = await tryKeyword(marker.name);
      if (nameResult) return nameResult;
    }

    return null;
  };

  const createRestaurantCard = (marker: MapMarker, isSelected: boolean = false): HTMLElement => {
    const card = document.createElement('div');
    card.className = `restaurant-card ${isSelected ? 'restaurant-card--selected' : ''}`;

    if (isSelected) {
      card.style.backgroundColor = '#FF6B35'; // appetite-stimulating orange
      card.style.color = '#FFFFFF';
      card.style.borderColor = '#FF6B35';
    }

    const header = document.createElement('div');
    header.className = 'restaurant-card__header';

    const titleWrap = document.createElement('div');
    titleWrap.className = 'restaurant-card__title-wrap';

    const categoryDot = document.createElement('div');
    categoryDot.className = 'restaurant-card__category-dot';
    if (isSelected) {
      categoryDot.style.backgroundColor = '#FFFFFF'; // white for contrast against orange
    }

    const title = document.createElement('h3');
    title.className = 'restaurant-card__title';
    title.textContent = marker.name || '음식점';
    if (isSelected) {
      title.style.color = '#FFFFFF';
    }

    titleWrap.appendChild(categoryDot);
    titleWrap.appendChild(title);

    const badge = document.createElement('div');
    badge.className = 'restaurant-card__badge';
    badge.textContent = '';

    header.appendChild(titleWrap);
    header.appendChild(badge);

    card.appendChild(header);

    return card;
  };

  const calculateClusters = (
    positions: Array<{ marker: MapMarker; position: any; coords: { lat: number; lng: number } }>,
    map: any,
    clusterDistance: number = 80
  ): ClusterGroup[] => {
    const { kakao } = window;
    if (!kakao?.maps || positions.length === 0) return [];

    const clusters: ClusterGroup[] = [];
    const assigned = new Set<number>();
    const proj = map.getProjection();

    for (let i = 0; i < positions.length; i++) {
      if (assigned.has(i)) continue;

      const cluster: ClusterGroup = {
        markers: [positions[i]],
        center: positions[i].coords,
      };
      assigned.add(i);

      const point1 = proj.pointFromCoords(positions[i].position);

      for (let j = i + 1; j < positions.length; j++) {
        if (assigned.has(j)) continue;

        const point2 = proj.pointFromCoords(positions[j].position);
        const dx = point1.x - point2.x;
        const dy = point1.y - point2.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < clusterDistance) {
          cluster.markers.push(positions[j]);
          assigned.add(j);
        }
      }

      if (cluster.markers.length > 1) {
        let sumLat = 0, sumLng = 0;
        cluster.markers.forEach(m => {
          sumLat += m.coords.lat;
          sumLng += m.coords.lng;
        });
        cluster.center = {
          lat: sumLat / cluster.markers.length,
          lng: sumLng / cluster.markers.length,
        };
      }

      clusters.push(cluster);
    }

    const multiClusters = clusters.filter(c => c.markers.length > 1);
    console.log('📊 클러스터 결과: 전체', clusters.length, '개 중', multiClusters.length, '개 그룹화됨');

    return clusters;
  };

  const createClusterOverlay = (cluster: ClusterGroup, onClick: () => void): HTMLElement => {
    const card = document.createElement('div');
    card.className = 'cluster-card';

    const header = document.createElement('div');
    header.className = 'cluster-card__header';

    const icon = document.createElement('div');
    icon.className = 'cluster-card__icon';
    icon.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#FF6B35"/>
      <circle cx="12" cy="9" r="2.5" fill="white"/>
    </svg>`;

    const text = document.createElement('span');
    text.className = 'cluster-card__text';
    text.textContent = `지역 맛집 ${cluster.markers.length}개`;

    header.appendChild(icon);
    header.appendChild(text);
    card.appendChild(header);

    card.style.cursor = 'pointer';
    card.addEventListener('click', onClick);

    return card;
  };

  const renderMarkersWithClustering = (
    positions: Array<{ marker: MapMarker; position: any; coords: { lat: number; lng: number } }>,
    map: any,
    currentLevel: number
  ) => {
    const { kakao } = window;
    if (!kakao?.maps) return;

    overlaysRef.current.forEach((overlay) => overlay.setMap(null));
    overlaysRef.current = [];
    mapMarkersRef.current = [];

    if (showUserLocation && userLocation) {
      const userPos = new kakao.maps.LatLng(userLocation.latitude, userLocation.longitude);

      const userMarkerWrapper = document.createElement('div');
      userMarkerWrapper.className = 'user-location-marker';
      userMarkerWrapper.innerHTML = `
        <div class="user-location-marker__pin">
          <span class="user-location-marker__icon">📍</span>
        </div>
      `;
      
      const userMarkerOverlay = new kakao.maps.CustomOverlay({
        position: userPos,
        yAnchor: 1.0,
        xAnchor: 0.5,
        content: userMarkerWrapper,
        zIndex: 1000,
      });
      userMarkerOverlay.setMap(map);
      overlaysRef.current.push(userMarkerOverlay);

      if (userLocation.label) {
        const overlay = new kakao.maps.CustomOverlay({
          position: userPos,
          yAnchor: 1.6,
          content: `<div style="padding:4px 8px;background:#DC2626;color:white;border-radius:8px;font-size:12px;font-weight:600;box-shadow:0 2px 6px rgba(220,38,38,0.3);">${userLocation.label}</div>`
        });
        overlay.setMap(map);
        overlay.setZIndex(1300);
        overlaysRef.current.push(overlay);
      }
    }

    if (currentLevel <= 2) {
      positions.forEach(({ marker: item, position }) => {
        const isFocused = !!(focusMarkerId && item.id === focusMarkerId);

        const markerWrapper = document.createElement('div');
        markerWrapper.className = `restaurant-marker ${isFocused ? 'restaurant-marker--selected' : ''}`;
        markerWrapper.style.width = '32px';
        markerWrapper.style.height = '48px';

        const pin = document.createElement('div');
        pin.className = 'restaurant-marker__pin';
        pin.style.width = '32px';
        pin.style.height = '48px';
        pin.style.background = 'transparent';
        pin.style.boxShadow = 'none';
        pin.style.border = 'none';
        pin.innerHTML = RESTAURANT_MARKER_SVG;

        const svg = pin.querySelector('svg');
        if (svg) {
          svg.style.width = '96px';
          svg.style.height = '126px';
          svg.style.display = 'block';
        }

        markerWrapper.appendChild(pin);

        const handleMarkerClick = () => {
          if (currentLevel > 2) {
            map.setLevel(2);
            map.setCenter(position);
            currentLevelRef.current = 2;
            userInteractedRef.current = true;
          }
          if (onMarkerClick) {
            onMarkerClick(item);
          }
        };

        markerWrapper.style.cursor = 'pointer';
        markerWrapper.addEventListener('click', handleMarkerClick);

        const markerOverlay = new kakao.maps.CustomOverlay({
          position,
          yAnchor: 1.0,
          xAnchor: 0.5,
          content: markerWrapper,
          zIndex: isFocused ? 1300 : 1200,
        });

        markerOverlay.setMap(map);
        overlaysRef.current.push(markerOverlay);

        if (item.name) {
          const card = createRestaurantCard(item, isFocused);
          card.style.cursor = 'pointer';
          card.addEventListener('click', handleMarkerClick);
          const cardOverlay = new kakao.maps.CustomOverlay({
            position,
            yAnchor: 1.0,
            xAnchor: 0.5,
            content: card,
            zIndex: isFocused ? 1400 : 1300,
          });
          cardOverlay.setMap(map);
          overlaysRef.current.push(cardOverlay);
        }
      });
    } else {
      const clusterDistance = Math.max(100, 60 + (currentLevel - 3) * 30);
      console.log('🔍 클러스터링 거리:', clusterDistance, '레벨:', currentLevel);
      const clusters = calculateClusters(positions, map, clusterDistance);

      clusters.forEach((cluster) => {
        if (cluster.markers.length === 1) {
          const { marker: item, position } = cluster.markers[0];
          const isFocused = !!(focusMarkerId && item.id === focusMarkerId);

          const markerWrapper = document.createElement('div');
          markerWrapper.className = `restaurant-marker ${isFocused ? 'restaurant-marker--selected' : ''}`;
          markerWrapper.style.width = '32px';
          markerWrapper.style.height = '48px';

          const pin = document.createElement('div');
          pin.className = 'restaurant-marker__pin';
          pin.style.width = '32px';
          pin.style.height = '48px';
          pin.style.background = 'transparent';
          pin.style.boxShadow = 'none';
          pin.style.border = 'none';
          pin.innerHTML = RESTAURANT_MARKER_SVG;

          const svg = pin.querySelector('svg');
          if (svg) {
            svg.style.width = '96px';
            svg.style.height = '126px';
            svg.style.display = 'block';
          }

          markerWrapper.appendChild(pin);

          const handleSingleMarkerClick = () => {
            map.setLevel(2);
            map.setCenter(position);
            currentLevelRef.current = 2;
            userInteractedRef.current = true;
            if (onMarkerClick) {
              onMarkerClick(item);
            }
          };

          markerWrapper.style.cursor = 'pointer';
          markerWrapper.addEventListener('click', handleSingleMarkerClick);

          const markerOverlay = new kakao.maps.CustomOverlay({
            position,
            yAnchor: 1.0,
            xAnchor: 0.5,
            content: markerWrapper,
            zIndex: isFocused ? 1300 : 1200,
          });

          markerOverlay.setMap(map);
          overlaysRef.current.push(markerOverlay);

          if (item.name) {
            const card = createRestaurantCard(item, isFocused);
            card.style.cursor = 'pointer';
            card.addEventListener('click', handleSingleMarkerClick);
            const cardOverlay = new kakao.maps.CustomOverlay({
              position,
              yAnchor: 1.0,
              xAnchor: 0.5,
              content: card,
              zIndex: isFocused ? 1400 : 1300,
            });
            cardOverlay.setMap(map);
            overlaysRef.current.push(cardOverlay);
          }
        } else {
          const clusterPosition = new kakao.maps.LatLng(cluster.center.lat, cluster.center.lng);

          const handleClusterClick = () => {
            map.setLevel(2);
            map.setCenter(clusterPosition);
            currentLevelRef.current = 2;
            userInteractedRef.current = true;
          };

          const clusterCard = createClusterOverlay(cluster, handleClusterClick);
          const clusterOverlay = new kakao.maps.CustomOverlay({
            position: clusterPosition,
            yAnchor: 0.5,
            xAnchor: 0.5,
            content: clusterCard,
            zIndex: 1500,
          });

          clusterOverlay.setMap(map);
          overlaysRef.current.push(clusterOverlay);
        }
      });
    }
  };

  const renderMarkers = async () => {
    const map = mapInstance.current;
    if (!map) return;
    const { kakao } = window;
    if (!kakao?.maps?.LatLngBounds) return;

    clearMapObjects();

    const markerItems = Array.isArray(markers) ? markers : [];
    const signature = markerItems.map((m) => m.id).join('|');
    if (signature !== lastMarkerSignatureRef.current) {
      lastMarkerSignatureRef.current = signature;
      if (!preserveView) {
        userInteractedRef.current = false;
      }
    }
    const bounds = new kakao.maps.LatLngBounds();
    const validPositions: Array<{ marker: MapMarker; position: any; coords: { lat: number; lng: number } }> = [];

    for (const item of markerItems) {
      const coords = await resolveCoordinates(item);
      if (!coords) continue;

      const position = new kakao.maps.LatLng(coords.lat, coords.lng);
      validPositions.push({ marker: item, position, coords });
      bounds.extend(position);
    }

    validPositionsRef.current = validPositions;

    if (showUserLocation && userLocation) {
      const userPos = new kakao.maps.LatLng(userLocation.latitude, userLocation.longitude);
      bounds.extend(userPos);
    }

    if (validPositions.length === 0 && userLocation) {
      map.setCenter(new kakao.maps.LatLng(userLocation.latitude, userLocation.longitude));
      map.setLevel(level);
      currentLevelRef.current = level;
      map.relayout();
      setMapLoaded(true);
      return;
    }

    if (validPositions.length === 0) {
      map.setLevel(level);
      currentLevelRef.current = level;
      map.relayout();
      setMapLoaded(true);
      return;
    }

    const shouldPreserveView = !!initialViewRef.current;
    const shouldAutoAdjust = !userInteractedRef.current;
    if (shouldPreserveView) {
      const view = initialViewRef.current;
      if (view) {
        map.setLevel(view.level);
        map.setCenter(new kakao.maps.LatLng(view.lat, view.lng));
        currentLevelRef.current = view.level;
      }
      initialViewRef.current = null;
      fullscreenViewRef.current = null;
      userInteractedRef.current = true;
    } else if (shouldAutoAdjust && fitBounds && (validPositions.length > 1 || userLocation)) {
      map.setBounds(bounds, 40, 40, 40, 40);
    } else if (shouldAutoAdjust && validPositions.length > 0) {
      const first = validPositions[0].position;
      map.setLevel(level);
      map.setCenter(first);
    }

    map.relayout();

    setTimeout(() => {
      const actualLevel = map.getLevel();
      currentLevelRef.current = actualLevel;
      console.log('🗺️ 클러스터링 적용 - 현재 레벨:', actualLevel, '마커 수:', validPositions.length);
      renderMarkersWithClustering(validPositions, map, actualLevel);
    }, 100);

    setMapLoaded(true);
  };

  const renderSingleLocation = async () => {
    const map = mapInstance.current;
    if (!map) {
      console.log('⚠️ renderSingleLocation: Map instance not available');
      return;
    }
    const { kakao } = window;
    if (!kakao?.maps?.LatLng) {
      console.log('⚠️ renderSingleLocation: Kakao Maps LatLng not available');
      return;
    }

    console.log('🗺️ renderSingleLocation called with:', { latitude, longitude, address, restaurantName });

    clearMapObjects();

    const placeMarker = (lat: number, lng: number) => {
      console.log(`📍 Placing marker at:`, { lat, lng });
      
      const center = new kakao.maps.LatLng(lat, lng);
      map.relayout();

      if (initialViewRef.current) {
        const { lat: savedLat, lng: savedLng, level: savedLevel } = initialViewRef.current;
        const preservedCenter = new kakao.maps.LatLng(savedLat, savedLng);
        map.setLevel(savedLevel);
        map.setCenter(preservedCenter);
        initialViewRef.current = null;
        fullscreenViewRef.current = null;
        userInteractedRef.current = true;
      } else {
        map.setLevel(level);
        map.setCenter(center);
      }

      const markerWrapper = document.createElement('div');
      markerWrapper.className = 'restaurant-marker restaurant-marker--selected';
      // Ensure inline styles match the desired SVG size
      markerWrapper.style.width = '32px';
      markerWrapper.style.height = '48px';

      const pin = document.createElement('div');
      pin.className = 'restaurant-marker__pin';
      // Ensure inline styles match the desired SVG size
      pin.style.width = '32px';
      pin.style.height = '48px';
      pin.style.background = 'transparent';
      pin.style.boxShadow = 'none';
      pin.style.border = 'none';

      // Use SVG directly
      pin.innerHTML = RESTAURANT_MARKER_SVG;
      const svg = pin.querySelector('svg');
      if (svg) {
        svg.style.width = '96px';
        svg.style.height = '126px';
        svg.style.display = 'block';
      }

      markerWrapper.appendChild(pin);

      const markerOverlay = new kakao.maps.CustomOverlay({
        position: center,
        yAnchor: 1.0,
        xAnchor: 0.5,
        content: markerWrapper,
        zIndex: 1200,
      });

      markerOverlay.setMap(map);
      overlaysRef.current.push(markerOverlay);

      if (restaurantName) {
        const marker: MapMarker = {
          id: 'single',
          name: restaurantName,
          address: address,
          subAdd1: subAdd1,
          subAdd2: subAdd2,
        };
        const card = createRestaurantCard(marker, true);
        const cardOverlay = new kakao.maps.CustomOverlay({
          position: center,
          yAnchor: 1.0,
          xAnchor: 0.5,
          content: card,
          zIndex: 1400,
        });
        cardOverlay.setMap(map);
        overlaysRef.current.push(cardOverlay);
      }

      console.log('✅ Marker placed successfully');
    };

    // DB에서 가져온 좌표가 있으면 바로 사용 (geocoding 건너뛰기)
    if (typeof latitude === 'number' && isFinite(latitude) &&
        typeof longitude === 'number' && isFinite(longitude)) {
      console.log('✅ Using coordinates from DB:', { latitude, longitude });
      placeMarker(latitude, longitude);
      setMapLoaded(true);
      return;
    }

    // 좌표가 없는 경우 에러 표시
    console.error('❌ No valid coordinates available');
    setError('위치 정보가 없습니다.');
    map.relayout();
    setMapLoaded(true);
  };

  useEffect(() => {
    if (!mapInstance.current || !window.kakao) {
      return;
    }
    
    if (!mapLoaded) {
      return;
    }
    
    let cancelled = false;

    const updateMap = async () => {
      if (!mapInstance.current) {
        return;
      }

      try {
        if (Array.isArray(markers)) {
          await renderMarkers();
        } else {
          await renderSingleLocation();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '지도 업데이트 중 오류가 발생했습니다.');
        }
      }
    };

    updateMap();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, focusMarkerId, latitude, longitude, address, level, restaurantName, subAdd1, subAdd2, userLocation, fitBounds, showUserLocation, preserveView, mapLoaded]);

  // 컨테이너 크기 변경 시 지도 크기 재조정
  useEffect(() => {
    if (mapInstance.current && mapLoaded) {
      const timer = setTimeout(() => {
        mapInstance.current.relayout();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [width, height, mapLoaded]);

  // 에러 상태 표시
  const containerStyle = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height
  };

  const handleFocusUserLocation = useCallback(() => {
    if (!mapInstance.current || !userLocation || !window.kakao) return;
    const { kakao } = window;
    const position = new kakao.maps.LatLng(userLocation.latitude, userLocation.longitude);
    mapInstance.current.setLevel(level);
    mapInstance.current.panTo(position);
    userInteractedRef.current = true;
  }, [level, userLocation]);

  const handleToggleFullscreen = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const map = mapInstance.current;
    if (map && window.kakao?.maps?.LatLng) {
      const center = map.getCenter();
      const view = {
        lat: center.getLat(),
        lng: center.getLng(),
        level: map.getLevel(),
      };
      initialViewRef.current = view;
      fullscreenViewRef.current = view;
    }
    if (!isFullscreen) {
      wrapper.requestFullscreen?.().catch(err => console.error('전체보기 실패:', err));
    } else if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(err => console.error('전체보기 종료 실패:', err));
    }
  }, [isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const nowFullscreen = document.fullscreenElement === wrapperRef.current;
      setIsFullscreen(nowFullscreen);

      if (fullscreenViewRef.current && mapInstance.current && window.kakao?.maps?.LatLng) {
        const { kakao } = window;
        const { lat, lng, level } = fullscreenViewRef.current;
        const center = new kakao.maps.LatLng(lat, lng);
        const map = mapInstance.current;
        map.setLevel(level);
        map.setCenter(center);
        map.relayout();
        initialViewRef.current = { lat, lng, level };
        userInteractedRef.current = true;
        fullscreenViewRef.current = null;
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (userLocation && !preserveView) {
      userInteractedRef.current = false;
    }
  }, [userLocation, userLocation?.latitude, userLocation?.longitude, preserveView]);

  if (error) {
    return (
      <div
        ref={wrapperRef}
        className={`${className} relative`}
        style={containerStyle}
      >
        <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col items-center justify-center p-6 rounded-lg border-2 border-dashed border-blue-200">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">지도를 불러올 수 없습니다</h3>
            <p className="text-sm text-gray-600 mb-1">{error}</p>
            <p className="text-xs text-gray-500">아래 버튼을 클릭해서 외부 지도에서 확인하세요</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
            <button
              onClick={() => {
                // address 우선 사용
                const searchQuery = address || '';
                const url = `https://map.kakao.com/link/search/${encodeURIComponent(searchQuery)}`;
                window.open(url, '_blank');
              }}
              className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              카카오맵에서 보기
            </button>
            <button
              onClick={() => {
                // address 우선 사용
                const searchQuery = address || '';
                const url = `https://map.naver.com/v5/search/${encodeURIComponent(searchQuery)}`;
                window.open(url, '_blank');
              }}
              className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              네이버지도에서 보기
            </button>
          </div>
          {address && (
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">검색어: <span className="font-medium">{address}</span></p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={`${className} relative`} style={containerStyle}>
      <div
        ref={mapContainer}
        className={`${!mapLoaded ? 'flex items-center justify-center bg-gray-50' : ''}`}
        style={{ width: '100%', height: '100%' }}
      >
        {!mapLoaded && !error && (
          <div className="text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-sm">카카오맵 로딩 중...</p>
          </div>
        )}
      </div>

      {mapLoaded && (
        <div className="absolute top-3 left-3 flex items-center gap-2 z-[20]">
          {userLocation && showUserLocation && (
            <button
              type="button"
              onClick={handleFocusUserLocation}
              className="px-3 py-2 rounded-md bg-white text-sm text-gray-700 shadow-md border border-gray-200 hover:bg-gray-50"
            >
              내 위치 보기
            </button>
          )}
          <button
            type="button"
            onClick={handleToggleFullscreen}
            className="px-3 py-2 rounded-md bg-white text-sm text-gray-700 shadow-md border border-gray-200 hover:bg-gray-50"
          >
            {isFullscreen ? '전체보기 종료' : '전체보기'}
          </button>
        </div>
      )}
    </div>
  );
};

export default KakaoMap;